import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { equipmentKeys } from '@/features/equipment/queries';
import { vesselsKeys } from '@/features/vessels/queries';
import { warrantyApi } from './service';
import type {
  ExtendWarrantyPayload,
  OverrideEquipmentWarrantyPayload,
  SetVesselWarrantyPayload,
} from './types';

export const warrantyKeys = {
  all: ['warranty'] as const,
  vesselSummary: (vesselId: string) =>
    [...warrantyKeys.all, 'vessel-summary', vesselId] as const,
  equipmentHistory: (equipmentId: string) =>
    [...warrantyKeys.all, 'equipment-history', equipmentId] as const,
};

export function useVesselWarrantySummary(vesselId: string | undefined) {
  return useQuery({
    queryKey: vesselId
      ? warrantyKeys.vesselSummary(vesselId)
      : warrantyKeys.all,
    queryFn: () => warrantyApi.vesselSummary(vesselId as string),
    enabled: Boolean(vesselId),
  });
}

export function useEquipmentWarrantyHistory(equipmentId: string | undefined) {
  return useQuery({
    queryKey: equipmentId
      ? warrantyKeys.equipmentHistory(equipmentId)
      : warrantyKeys.all,
    queryFn: () => warrantyApi.history(equipmentId as string),
    enabled: Boolean(equipmentId),
  });
}

export function useSetVesselWarranty(vesselId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetVesselWarrantyPayload) =>
      warrantyApi.setVesselWarranty(vesselId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: warrantyKeys.vesselSummary(vesselId) });
      qc.invalidateQueries({ queryKey: vesselsKeys.detail(vesselId) });
      // Equipment dates may have cascaded — wipe the equipment caches too.
      qc.invalidateQueries({ queryKey: equipmentKeys.all });
    },
  });
}

export function useOverrideEquipmentWarranty(
  equipmentId: string,
  vesselId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OverrideEquipmentWarrantyPayload) =>
      warrantyApi.overrideEquipment(equipmentId, input),
    onSuccess: (updated) => {
      qc.setQueryData(equipmentKeys.detail(equipmentId), updated);
      if (vesselId) {
        qc.invalidateQueries({
          queryKey: warrantyKeys.vesselSummary(vesselId),
        });
      }
    },
  });
}

export function useResetEquipmentWarranty(
  equipmentId: string,
  vesselId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => warrantyApi.resetEquipment(equipmentId),
    onSuccess: (updated) => {
      qc.setQueryData(equipmentKeys.detail(equipmentId), updated);
      if (vesselId) {
        qc.invalidateQueries({
          queryKey: warrantyKeys.vesselSummary(vesselId),
        });
      }
    },
  });
}

export function useExtendEquipmentWarranty(
  equipmentId: string,
  vesselId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExtendWarrantyPayload) =>
      warrantyApi.extendEquipment(equipmentId, input),
    onSuccess: (out) => {
      qc.setQueryData(equipmentKeys.detail(equipmentId), out.equipment);
      qc.invalidateQueries({
        queryKey: warrantyKeys.equipmentHistory(equipmentId),
      });
      if (vesselId) {
        qc.invalidateQueries({
          queryKey: warrantyKeys.vesselSummary(vesselId),
        });
      }
    },
  });
}
